/**
 * Auth context — minimal client-side session tracking.
 *
 * The actual credential is the HttpOnly `cspro_session` cookie set by
 * /api/auth/verify; JS cannot read it directly. We track session presence
 * indirectly via a sessionStorage marker that the callback page sets after
 * a successful verify. When the provider mounts, it validates that marker
 * against /api/auth/me; a 401 means the cookie is no longer valid and the
 * protected route redirects to /signin.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  ReactNode,
} from "react";
import { ApiError, authApi } from "./api.ts";
import { resetSharedAssistantConfig } from "./appearance-context.tsx";
import { resetSharedEntitlements } from "../hooks/useEntitlements.ts";
import { clearOperatorInboxDrafts } from "./inboxDrafts.ts";

const STORAGE_KEY = "cspro.session.hint";
const SESSION_VALIDATION_TIMEOUT_MS = 2_000;

export interface SessionHint {
  email: string;
  tenantId: string;
  signedInAt: number;
}

function isSessionHint(value: unknown): value is SessionHint {
  if (typeof value !== "object" || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return typeof candidate.email === "string"
    && candidate.email.trim().length > 0
    && typeof candidate.tenantId === "string"
    && candidate.tenantId.trim().length > 0
    && typeof candidate.signedInAt === "number"
    && Number.isFinite(candidate.signedInAt);
}

interface AuthContextValue {
  session: SessionHint | null;
  setSession: (hint: SessionHint | null) => void;
  isLoading: boolean;
  signOut: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSessionState] = useState<SessionHint | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const authRevision = useRef(0);

  useEffect(() => {
    let active = true;
    let validationController: AbortController | null = null;
    let validationTimeout: ReturnType<typeof setTimeout> | null = null;
    const validationRevision = authRevision.current;
    const isCurrent = () =>
      active && authRevision.current === validationRevision;

    const clearStoredHint = () => {
      try {
        sessionStorage.removeItem(STORAGE_KEY);
      } catch {
        // Storage unavailable. State still becomes signed-out below.
      }
    };

    const initialize = async () => {
      let hint: SessionHint | null = null;

      try {
        const raw = sessionStorage.getItem(STORAGE_KEY);
        if (raw !== null) {
          const parsed: unknown = JSON.parse(raw);
          if (isSessionHint(parsed)) {
            hint = parsed;
          } else {
            clearStoredHint();
          }
        }
      } catch {
        // Storage unavailable or the marker is malformed. Treat as signed-out;
        // the cookie remains authoritative for every API request.
        clearStoredHint();
      }

      if (!hint) {
        if (active) setIsLoading(false);
        return;
      }

      try {
        validationController = new AbortController();
        validationTimeout = setTimeout(
          () => validationController?.abort(),
          SESSION_VALIDATION_TIMEOUT_MS,
        );
        const response = await authApi.me(validationController.signal);
        const authenticatedIdentity = response.ok ? response.result : null;
        const authenticatedEmail =
          typeof authenticatedIdentity?.email === "string"
            ? authenticatedIdentity.email
            : "";
        const authenticatedTenantId =
          typeof authenticatedIdentity?.tenant_id === "string"
            ? authenticatedIdentity.tenant_id
            : "";
        const emailMatches =
          authenticatedEmail.trim().toLowerCase() ===
          hint.email.trim().toLowerCase();
        const tenantMatches =
          authenticatedTenantId === hint.tenantId;

        if (!isCurrent()) return;
        if (!emailMatches || !tenantMatches) {
          clearStoredHint();
          setSessionState(null);
        } else {
          setSessionState(hint);
        }
      } catch (error) {
        if (!isCurrent()) return;
        if (
          error instanceof ApiError &&
          (error.status === 401 || error.status === 403)
        ) {
          clearStoredHint();
          setSessionState(null);
        } else {
          // A temporary network/server failure must not manufacture a logout.
          // The protected APIs continue to enforce the HttpOnly cookie.
          setSessionState(hint);
        }
      } finally {
        if (validationTimeout !== null) clearTimeout(validationTimeout);
        if (active) setIsLoading(false);
      }
    };

    void initialize();

    return () => {
      active = false;
      if (validationTimeout !== null) clearTimeout(validationTimeout);
      validationController?.abort();
    };
  }, []);

  const setSession = useCallback((hint: SessionHint | null) => {
    authRevision.current += 1;
    // Drop any in-flight assistant-config read on EVERY session transition,
    // not only on sign-out. It is a module-global slot with no tenant key, and
    // a session change here is a React state change rather than a document
    // load, so a stalled GET would otherwise still be joinable by the next
    // tenant's providers -- and paint them the previous tenant's brand,
    // company name and persona. Every new session is installed through this
    // function (sign-in, the callback page, and signOut's own `setSession
    // (null)`), which is what makes the invariant true by construction rather
    // than by remembering to call it. The expiry paths in `initialize` go to
    // a signed-out state with no tenant to mispaint, and the session that
    // replaces them arrives here. A same-tenant refresh clearing the slot is
    // harmless: callers already holding the promise still resolve, and the
    // next mount simply issues a fresh read. See appearance-context.
    resetSharedAssistantConfig();
    // The entitlements slot is the same construct with the same hazard: a
    // stalled GET /api/billing/entitlements joined by the next tenant's
    // TeamPanel or ChannelsPanel paints them the previous tenant's plan and
    // seat count, and decides a gated control on it. See hooks/useEntitlements.
    resetSharedEntitlements();
    setSessionState(hint);
    setIsLoading(false);
    try {
      if (hint) {
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(hint));
      } else {
        sessionStorage.removeItem(STORAGE_KEY);
      }
    } catch {
      // Silent — see above.
    }
  }, []);

  const signOut = useCallback(() => {
    const signedInSession = session;
    if (signedInSession) {
      try {
        const storage = window.sessionStorage;
        clearOperatorInboxDrafts(
          storage,
          signedInSession.tenantId,
          signedInSession.email,
        );
      } catch {
        // Draft cleanup is best-effort and must never block sign-out.
      }
    }
    // Clear the local hint immediately so the UI reflects sign-out at once.
    // This also drops the shared assistant-config slot: the reset lives in
    // `setSession`, which this call reaches unconditionally.
    setSession(null);
    // Server-side logout (Wave 2 #22): POST /api/auth/logout both clears the
    // HttpOnly cookie (Set-Cookie max-age=0) AND denylists the token's jti, so
    // a copied 30-day cookie can't be replayed after sign-out. Fire-and-forget
    // + keepalive so it still lands if the tab navigates away right after.
    try {
      void fetch("/api/auth/logout", {
        method: "POST",
        credentials: "include",
        keepalive: true,
      }).catch(() => {
        // Best-effort: even if the request fails, the local hint is cleared and
        // the cookie still expires server-side at its exp.
      });
    } catch {
      // fetch unavailable (very old/locked-down env) — local clear still holds.
    }
  }, [session, setSession]);

  const value = useMemo(
    () => ({ session, setSession, isLoading, signOut }),
    [session, setSession, isLoading, signOut],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}
